import React, { useState, useCallback } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import { receiptService } from '../services/ReceiptService';
import { useAuth } from '../contexts/AuthContext';
import { ModernCard } from './ModernCards';

interface OCRData {
  extracted_amount: string | number | null | undefined;
  extracted_date: string | null | undefined;
  extracted_vendor: string | null | undefined;
  extracted_invoice_id: string | null | undefined;
  category_id?: number | null;
  policy_id?: number | null;
  category?: {
    id: number;
    name: string;
    description?: string;
  } | null;
  policy?: {
    id: number;
    name: string;
    description?: string;
  } | null;
}

interface ReceiptUploadStepProps {
  onReceiptAnalyzed: (receiptId: number, ocrData: OCRData) => void;
  onNext: () => void;
}

const ReceiptUploadStep: React.FC<ReceiptUploadStepProps> = ({
  onReceiptAnalyzed,
  onNext
}) => {
  const { token } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [ocrData, setOcrData] = useState<OCRData | null>(null);
  const [receiptId, setReceiptId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFileUpload = useCallback(async (file: File) => {
    if (!file) return;

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', `Receipt upload for expense creation - ${file.name}`);

      const response = await receiptService.uploadReceipt(file, token || '');
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.success && response.receipt_id) {
        setReceiptId(response.receipt_id);
        
        // Use the AI recommendations from the upload response
        const ocrData: OCRData = {
          extracted_amount: response.extracted_amount,
          extracted_date: response.extracted_date,
          extracted_vendor: response.extracted_vendor,
          extracted_invoice_id: response.extracted_invoice_id,
          category_id: response.category_id,
          policy_id: response.policy_id,
          category: response.category,
          policy: response.policy
        };
        
        setOcrData(ocrData);
        onReceiptAnalyzed(response.receipt_id, ocrData);
      } else {
        setError(response.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload receipt');
    } finally {
      setIsUploading(false);
    }
  }, [onReceiptAnalyzed, token]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  }, [handleFileUpload]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  }, [handleFileUpload]);

  const handleContinue = () => {
    if (receiptId && ocrData) {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      {!ocrData && (
        <ModernCard className="p-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 font-display">Upload Receipt</h2>
            <p className="text-gray-600">
              Upload a receipt image and we'll automatically extract the key information for you
            </p>
          </div>

          <div
            className={`border-2 border-dashed rounded-xl p-12 text-center transition-all duration-200 ${
              dragActive 
                ? 'border-green-500 bg-green-50' 
                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {isUploading ? (
              <div className="space-y-6">
                <Loader2 className="h-16 w-16 text-green-600 mx-auto animate-spin" />
                <div className="space-y-3">
                  <p className="text-xl font-semibold text-gray-900">Processing Receipt...</p>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-green-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    {uploadProgress < 100 ? 'Uploading and analyzing...' : 'Analysis complete!'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <Upload className="h-10 w-10 text-green-600" />
                </div>
                <div>
                  <p className="text-xl font-semibold text-gray-900 mb-3">
                    Drag and drop your receipt here
                  </p>
                  <p className="text-gray-600 mb-6">or</p>
                  <label className="btn-primary inline-flex items-center space-x-2 cursor-pointer">
                    <FileText className="h-5 w-5" />
                    <span>Choose File</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*,.pdf"
                      onChange={handleFileInput}
                    />
                  </label>
                </div>
                <p className="text-sm text-gray-500">
                  Supports JPG, PNG, PDF files up to 10MB
                </p>
              </div>
            )}
          </div>
        </ModernCard>
      )}

      {/* OCR Results */}
      {ocrData && (
        <div className="space-y-6">
          <ModernCard className="p-6 bg-green-50 border-green-200">
            <div className="flex items-center">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
              <div>
                <h3 className="text-xl font-semibold text-green-800">
                  Receipt Analysis Complete!
                </h3>
                <p className="text-green-700 mt-1">
                  We've extracted the following information from your receipt:
                </p>
              </div>
            </div>
          </ModernCard>

          <ModernCard className="p-6">
            <h4 className="text-xl font-semibold text-gray-900 mb-6 font-display">Extracted Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    {ocrData.extracted_amount ? (
                      <span className="text-2xl font-bold text-green-600">
                        ${Number(ocrData.extracted_amount).toFixed(2)}
                      </span>
                    ) : (
                      <span className="text-gray-500 italic">Not detected</span>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vendor</label>
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    {ocrData.extracted_vendor ? (
                      <span className="text-lg text-gray-900">{ocrData.extracted_vendor}</span>
                    ) : (
                      <span className="text-gray-500 italic">Not detected</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    {ocrData.extracted_date ? (
                      <span className="text-lg text-gray-900">
                        {new Date(ocrData.extracted_date).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="text-gray-500 italic">Not detected</span>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Number</label>
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    {ocrData.extracted_invoice_id ? (
                      <span className="text-lg text-gray-900">{ocrData.extracted_invoice_id}</span>
                    ) : (
                      <span className="text-gray-500 italic">Not detected</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </ModernCard>

          <div className="flex justify-between">
            <button
              onClick={() => {
                setOcrData(null);
                setReceiptId(null);
                setError(null);
              }}
              className="px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg font-medium transition-colors"
            >
              Upload Different Receipt
            </button>
            
            <button
              onClick={handleContinue}
              className="btn-primary flex items-center space-x-2"
            >
              <span>Continue to Expense Details</span>
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <ModernCard className="p-6 bg-red-50 border-red-200">
          <div className="flex items-center">
            <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </ModernCard>
      )}
    </div>
  );
};

export default ReceiptUploadStep;
